// src/app/components/admin/framework-library/framework-library-page.tsx
//
// Admin tools → Framework library (#139). Every client's approved framework,
// anonymised (no client name, competitor names, URLs, quoted evidence, figures
// or proper nouns), grouped by category, with what it measured and which model
// judged it: presence share and primary-when-present per belief, which beliefs
// calibrated clean first time, which turned out to be category defaults, and
// which question forms surfaced which beliefs.
//
// Stage 2 is given the best templates of a new brand's category as its
// starting point. Here an admin marks a template as a recommended starting
// point or excludes it, for everyone or for one brand, and records a client's
// opt-out. Templates are read-only history: there is nothing to edit; a
// correction is a new approved framework.
//
// Laid out on the admin portal's shell, like the Brand record: categories in
// the left column, the category in the content column.

import { Fragment, useCallback, useEffect, useState, type ReactNode } from "react";
import { ChevronRight, Library, Loader2, PanelLeft, RefreshCw, ShieldCheck, X } from "lucide-react";

import {
  frameworkLibraryApi,
  type LibraryCategory,
  type LibraryClient,
  type LibraryView,
  type TemplateDetail,
  type TemplateMark,
  type TemplateSummary,
} from "@/api/framework-library";
import { useResponsive } from "@/app/hooks/common/use-breakpoint";
import { Button, ErrorNote, Input, Section, helpStyle, selectClass } from "../wind-tunnel/wind-tunnel-shared";

const ACCENT = "var(--alpha-teal)";
const SOURCE_LABEL: Record<string, string> = {
  first_approval: "First approval",
  relock: "Re-approval",
  rebaseline: "Rebaseline",
  current: "Backfill",
  before: "Backfill (earlier segment)",
};

function pct(value: number | null | undefined): string {
  return value === null || value === undefined ? "Not measured" : `${(value * 100).toFixed(1)}%`;
}

function day(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function categoryTitle(c: LibraryCategory): string {
  return c.category || "No category recorded";
}

function judgeLabel(models: string[] | null | undefined): string {
  return models && models.length > 0 ? models.join(", ") : "Not measured";
}

function Pill({ children, color }: { children: ReactNode; color?: string }) {
  return (
    <span style={{ padding: "1px 8px", borderRadius: 999, fontSize: 11.5, fontWeight: 600, whiteSpace: "nowrap",
      color: color || "var(--muted-foreground)", border: `1px solid ${color || "var(--border)"}` }}>
      {children}
    </span>
  );
}

function MarkPill({ mark }: { mark: TemplateMark | null }) {
  if (mark === "recommended") return <Pill color="var(--signal-green)">Recommended</Pill>;
  if (mark === "excluded") return <Pill color="var(--infrared-coral)">Excluded</Pill>;
  return null;
}

const th = { textAlign: "left" as const, padding: "6px 10px", fontSize: 12, fontWeight: 600, color: "var(--muted-foreground)", borderBottom: "1px solid var(--border)" };
const td = { padding: "8px 10px", fontSize: 13, borderBottom: "1px solid var(--border)", verticalAlign: "top" as const };

function Table({ head, children }: { head: string[]; children: ReactNode }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr>{head.map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function Loading() {
  return (
    <div className="flex items-center gap-2" style={helpStyle}>
      <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} /> Loading…
    </div>
  );
}

// ── One template, in full ───────────────────────────────────────────────────

function TemplateDetailView({ id }: { id: number }) {
  const [detail, setDetail] = useState<TemplateDetail | null>(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    frameworkLibraryApi.template(id).then(setDetail).catch((e) => setErr(String(e?.message || e)));
  }, [id]);
  if (err) return <ErrorNote>{err}</ErrorNote>;
  if (!detail) return <Loading />;
  const perf = detail.performance || {};
  const cal = perf.calibration?.beliefs || {};
  const labels = Object.fromEntries(detail.content.beliefs.map((b) => [b.id, b.label || b.id]));
  return (
    <div className="flex flex-col gap-4" style={{ marginTop: 12 }}>
      <p style={helpStyle}>
        Judged by:{" "}
        {Object.keys(perf.judges || {}).length === 0 ? "Not measured" : Object.entries(perf.judges || {})
          .map(([model, answers]) => `${model} (${answers} answers)`).join(", ")}
      </p>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Beliefs and anchors</div>
        <Table head={["Belief", "Anchors (0 / 1 / 2)", "Presence", "Primary when present", "First calibration", "Category default"]}>
          {detail.content.beliefs.map((b) => {
            const cell = perf.beliefs?.[b.id];
            const c = cal[b.id];
            return (
              <tr key={b.id}>
                <td style={td}>
                  <div style={{ fontWeight: 600 }}>{b.label || b.id}</div>
                  <div style={helpStyle}>{b.claim}</div>
                  <div style={{ ...helpStyle, marginTop: 2 }}>
                    Evidence: {b.evidence_shape.quotes} quote{b.evidence_shape.quotes === 1 ? "" : "s"}
                    {` (${b.evidence_shape.from_pages} from pages, ${b.evidence_shape.from_documents} from documents)`}
                  </div>
                </td>
                <td style={{ ...td, ...helpStyle }}>
                  <div>0: {b.anchors.score_0}</div>
                  <div>1: {b.anchors.score_1}</div>
                  <div>2: {b.anchors.score_2}</div>
                </td>
                <td style={td} title={cell ? `${cell.present} of ${cell.answers} answers` : undefined}>
                  {pct(cell?.answers ? cell.share : null)}{cell?.directional ? " (directional)" : ""}
                </td>
                <td style={td}>{pct(cell?.present ? cell.primary_when_present : null)}</td>
                <td style={td}>{!c ? "Not calibrated" : c.clean_first_time ? "Clean" : `Flagged: ${c.first_flags.join(", ")}`}</td>
                <td style={td}>{b.category_default === null ? "Not checked" : b.category_default ? "Yes" : "No"}</td>
              </tr>
            );
          })}
        </Table>
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Personas</div>
        <Table head={["Persona", "Use case", "Description", "Goal"]}>
          {detail.content.personas.map((p) => (
            <tr key={p.id}>
              <td style={td}>{p.label || p.id}</td>
              <td style={td}>{p.use_case || "—"}</td>
              <td style={{ ...td, ...helpStyle }}>{p.description}</td>
              <td style={{ ...td, ...helpStyle }}>{p.goal}</td>
            </tr>
          ))}
        </Table>
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Questions and what they surfaced</div>
        <Table head={["Form", "Persona", "Question", "Written for", "Surfaced"]}>
          {detail.content.battery.queries.map((q) => {
            const cell = perf.questions?.[q.id];
            const surfaced = Object.entries(cell?.beliefs || {}).sort((a, b) => b[1] - a[1]);
            return (
              <tr key={q.id}>
                <td style={td}>{q.form}</td>
                <td style={td}>{q.persona_id}</td>
                <td style={{ ...td, maxWidth: 380 }}>{q.text}</td>
                <td style={{ ...td, ...helpStyle }}>{q.belief_ids.map((id) => labels[id] || id).join(", ")}</td>
                <td style={{ ...td, ...helpStyle }}>
                  {!cell ? "Not measured" : surfaced.length === 0 ? "Nothing" : surfaced.map(([id, share]) => (
                    <div key={id}>
                      {labels[id] || id} {pct(share)}{cell.unprompted.includes(id) ? " (not asked for)" : ""}
                    </div>
                  ))}
                </td>
              </tr>
            );
          })}
        </Table>
      </div>
      <p style={helpStyle}>
        Name variants: {detail.content.name_variants.count} ({detail.content.name_variants.pattern.join(", ") || "none"});
        {` ${detail.content.name_variants.misidentification_gates} misidentification gate(s).`}
      </p>
    </div>
  );
}

// ── Marking ─────────────────────────────────────────────────────────────────

function BrandExclusion({ template, clients, onChanged }: {
  template: TemplateSummary; clients: LibraryClient[]; onChanged: () => void;
}) {
  const [brand, setBrand] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const keptOut = clients.filter((c) => c.excluded_templates.includes(template.id));
  const run = (call: Promise<unknown>) => {
    setBusy(true);
    setErr("");
    call.then(() => { setBrand(""); onChanged(); })
      .catch((e) => setErr(String(e?.message || e)))
      .finally(() => setBusy(false));
  };
  return (
    <div className="flex flex-col gap-2">
      <div style={{ fontSize: 13, fontWeight: 600 }}>Keep out of one brand's generations</div>
      <p style={helpStyle}>Covers every revision of this template. Other brands can still be given it.</p>
      {keptOut.map((c) => (
        <div key={c.customer_id} className="flex flex-wrap items-center gap-2" style={{ fontSize: 13 }}>
          <span>{c.brand_name}</span>
          <Button disabled={busy} onClick={() => run(frameworkLibraryApi.includeTemplate(c.customer_id, template.id))}>
            Allow again
          </Button>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <div style={{ flex: 1, minWidth: 220 }}>
          <select aria-label="Brand" className={selectClass} value={brand} onChange={(e) => setBrand(e.target.value)}>
            <option value="">Pick a brand</option>
            {clients.filter((c) => !keptOut.includes(c)).map((c) => (
              <option key={c.customer_id} value={c.customer_id}>{c.brand_name}</option>
            ))}
          </select>
        </div>
        <Button
          disabled={busy || !brand}
          onClick={() => run(frameworkLibraryApi.excludeTemplate(Number(brand), template.id, ""))}
        >
          Keep out
        </Button>
      </div>
      {err && <ErrorNote>{err}</ErrorNote>}
    </div>
  );
}

function MarkControls({ template, onMarked }: { template: TemplateSummary; onMarked: () => void }) {
  const [reason, setReason] = useState("");
  const [excluding, setExcluding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const send = (mark: TemplateMark | null, why = "") => {
    setBusy(true);
    setErr("");
    frameworkLibraryApi.mark(template.id, mark, why)
      .then(() => { setExcluding(false); setReason(""); onMarked(); })
      .catch((e) => setErr(String(e?.message || e)))
      .finally(() => setBusy(false));
  };
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        {template.mark !== "recommended" && (
          <Button disabled={busy} onClick={() => send("recommended")}>Recommend as a starting point</Button>
        )}
        {template.mark !== "excluded" && !excluding && (
          <Button disabled={busy} variant="danger" onClick={() => setExcluding(true)}>Exclude</Button>
        )}
        {template.mark && <Button disabled={busy} onClick={() => send(null)}>Clear mark</Button>}
      </div>
      {excluding && (
        <div className="flex flex-wrap items-center gap-2">
          <div style={{ flex: 1, minWidth: 220 }}>
            <Input placeholder="Why it should not be a starting point" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <Button disabled={busy || !reason.trim()} variant="danger" onClick={() => send("excluded", reason.trim())}>Exclude</Button>
          <Button disabled={busy} onClick={() => setExcluding(false)}>Cancel</Button>
        </div>
      )}
      {err && <ErrorNote>{err}</ErrorNote>}
    </div>
  );
}

// ── A category ──────────────────────────────────────────────────────────────

function CategoryView({ category, clients, onChanged, onClientsChanged }: {
  category: LibraryCategory; clients: LibraryClient[]; onChanged: () => void; onClientsChanged: () => void;
}) {
  const [open, setOpen] = useState<number | null>(null);
  const measured = category.beliefs.filter((b) => b.presence !== null)
    .sort((a, b) => (b.presence ?? 0) - (a.presence ?? 0));
  return (
    <div className="flex flex-col gap-4">
      <Section
        title={categoryTitle(category)}
        icon={<Library style={{ width: 16, height: 16, color: ACCENT }} />}
        hint={`${category.vertical_label || category.vertical || "No vertical"} · ${category.templates.length} template${category.templates.length === 1 ? "" : "s"}. Client names, competitors, URLs, quoted evidence, figures and proper nouns are stripped from every template.`}
      >
        {category.templates.length === 0 ? (
          <p style={helpStyle}>No approved framework in this category yet.</p>
        ) : (
          <Table head={["Template", "Approved", "Reads", "Judged by", "Mean presence", "Clean first calibration", "Category defaults", ""]}>
            {category.templates.map((t) => (
              <Fragment key={t.id}>
                <tr>
                  <td style={td}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span style={{ fontWeight: 600 }}>#{t.id}</span>
                      <span style={helpStyle}>{SOURCE_LABEL[t.source_detail] || t.source}</span>
                      {t.generated && <Pill>Generated</Pill>}
                      <MarkPill mark={t.mark} />
                    </div>
                    <div style={helpStyle}>{t.beliefs} beliefs · {t.personas} personas · {t.questions} questions</div>
                    {t.mark === "excluded" && t.mark_reason && <div style={helpStyle}>Excluded: {t.mark_reason}</div>}
                  </td>
                  <td style={td}>{day(t.approved_at)}</td>
                  <td style={td} title={`${t.answers} answers`}>{t.reads}</td>
                  <td style={{ ...td, ...helpStyle }}>{judgeLabel(Object.keys(t.judges || {}))}</td>
                  <td style={td}>{pct(t.mean_presence)}</td>
                  <td style={td}>
                    {t.calibrations ? `${t.beliefs_clean_first_time ?? 0} of ${t.beliefs_calibrated ?? 0}` : "Not calibrated"}
                  </td>
                  <td style={td}>{t.category_defaults ?? 0}</td>
                  <td style={td}>
                    <button
                      onClick={() => setOpen(open === t.id ? null : t.id)}
                      style={{ border: "none", background: "none", color: ACCENT, cursor: "pointer", fontSize: 13, fontWeight: 600 }}
                    >
                      {open === t.id ? "Hide" : "Open"}
                    </button>
                  </td>
                </tr>
                {open === t.id && (
                  <tr>
                    <td colSpan={8} style={{ ...td, backgroundColor: "var(--muted-30)" }}>
                      <div className="flex flex-col gap-4">
                        <MarkControls template={t} onMarked={onChanged} />
                        <BrandExclusion template={t} clients={clients} onChanged={onClientsChanged} />
                      </div>
                      <TemplateDetailView id={t.id} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </Table>
        )}
      </Section>

      <Section
        title="Belief performance"
        hint="Every measured belief of every template in this category, highest presence first. Presence is the share of answers in which the judge found the belief; primary when present is how often the brand was the primary recommendation in those answers. Figures from different judges are not directly comparable."
      >
        {measured.length === 0 ? (
          <p style={helpStyle}>No completed read on any template in this category yet.</p>
        ) : (
          <Table head={["Belief", "Template", "Judged by", "Presence", "Primary when present", "First calibration", "Category default"]}>
            {measured.map((b) => (
              <tr key={`${b.template_id}-${b.id}`}>
                <td style={td}>
                  <div style={{ fontWeight: 600 }}>{b.label || b.id}</div>
                  <div style={helpStyle}>{b.claim}</div>
                </td>
                <td style={td}>#{b.template_id}</td>
                <td style={{ ...td, ...helpStyle }}>{judgeLabel(b.judges)}</td>
                <td style={td} title={`${b.answers} answers`}>{pct(b.presence)}{b.directional ? " (directional)" : ""}</td>
                <td style={td}>{pct(b.primary_when_present)}</td>
                <td style={td}>
                  {b.clean_first_time === null || b.clean_first_time === undefined ? "Not calibrated"
                    : b.clean_first_time ? "Clean" : `Flagged: ${(b.first_flags || []).join(", ")}`}
                </td>
                <td style={td}>{b.category_default === null ? "Not checked" : b.category_default ? "Yes" : "No"}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section
        title="Onboardings"
        hint="Each brand onboarded in this category from a Stage 2 generation, oldest first: how many library templates the generation started from, how many steps or cells were regenerated, and how many edits the reviewer made before the first approval."
      >
        {category.onboardings.length === 0 ? (
          <p style={helpStyle}>No onboarding in this category has been generated with Stage 2 yet.</p>
        ) : (
          <Table head={["Onboarding", "Library templates", "Regenerations", "Edits before approval", "By part"]}>
            {category.onboardings.map((o, i) => (
              <tr key={i}>
                <td style={td}>
                  <div style={{ fontWeight: 600 }}>{i + 1}</div>
                  <div style={helpStyle}>{o.approved_at ? `Approved ${day(o.approved_at)}` : `Generated ${day(o.generated_at)}, not approved yet`}</div>
                </td>
                <td style={td}>{o.library_templates}</td>
                <td style={td}>{o.regenerations}</td>
                <td style={td}>{o.edit_count ?? "—"}</td>
                <td style={{ ...td, ...helpStyle }}>
                  {o.edit_count === null ? "—" : Object.entries(o.by_part).map(([part, n]) => `${part} ${n}`).join(" · ")}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Section>
    </div>
  );
}

// ── Client permissions ──────────────────────────────────────────────────────

function ClientRow({ client, onChanged }: { client: LibraryClient; onChanged: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const send = (optedOut: boolean) => {
    setBusy(true);
    setErr("");
    frameworkLibraryApi.setOptOut(client.customer_id, optedOut)
      .then(() => { setConfirming(false); onChanged(); })
      .catch((e) => setErr(String(e?.message || e)))
      .finally(() => setBusy(false));
  };
  return (
    <tr>
      <td style={td}>{client.brand_name}</td>
      <td style={td}>
        {client.opted_out
          ? <Pill color="var(--infrared-coral)">Opted out</Pill>
          : <span style={helpStyle}>Allowed</span>}
        {client.opted_out && client.opted_out_at && (
          <div style={helpStyle}>{day(client.opted_out_at)}{client.opted_out_by ? ` by ${client.opted_out_by}` : ""}</div>
        )}
      </td>
      <td style={td}>
        {client.opted_out ? (
          <Button disabled={busy} onClick={() => send(false)}>Allow again</Button>
        ) : confirming ? (
          <div className="flex flex-wrap items-center gap-2">
            <span style={helpStyle}>Deletes every template made from this client's frameworks.</span>
            <Button disabled={busy} variant="danger" onClick={() => send(true)}>Opt out</Button>
            <Button disabled={busy} onClick={() => setConfirming(false)}>Cancel</Button>
          </div>
        ) : (
          <Button disabled={busy} variant="danger" onClick={() => setConfirming(true)}>Opt out</Button>
        )}
        {err && <ErrorNote>{err}</ErrorNote>}
      </td>
    </tr>
  );
}

function ClientPermissions({ clients, onChanged }: { clients: LibraryClient[]; onChanged: () => void }) {
  return (
    <Section
      title="Client permissions"
      icon={<ShieldCheck style={{ width: 16, height: 16, color: ACCENT }} />}
      hint="A client whose CSA does not allow derivative use is opted out here: its frameworks make no templates and the ones it has are deleted. Allowing it again brings nothing back until its next approval or a backfill. Prospects and demo brands are never templates."
    >
      <Table head={["Client", "Library use", ""]}>
        {clients.filter((c) => !c.kind || c.kind === "real").map((c) => (
          <ClientRow key={c.customer_id} client={c} onChanged={onChanged} />
        ))}
      </Table>
    </Section>
  );
}

// ── The page ────────────────────────────────────────────────────────────────

export function FrameworkLibraryPage() {
  const { isDesktop } = useResponsive();
  const [view, setView] = useState<LibraryView | null>(null);
  const [clients, setClients] = useState<LibraryClient[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [showPermissions, setShowPermissions] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    frameworkLibraryApi.view().then(setView).catch((e) => setErr(String(e?.message || e)));
  }, []);
  const loadClients = useCallback(() => {
    frameworkLibraryApi.clients().then((r) => setClients(r.clients)).catch((e) => setErr(String(e?.message || e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (view?.available) loadClients(); }, [view?.available, loadClients]);
  useEffect(() => { if (isDesktop) setDrawerOpen(false); }, [isDesktop]);

  const refresh = () => {
    setRefreshing(true);
    frameworkLibraryApi.refresh().then(load).catch((e) => setErr(String(e?.message || e))).finally(() => setRefreshing(false));
  };

  const categories = view?.categories || [];
  const active = showPermissions ? null : categories.find((c) => c.key === selected) || null;
  const withheld = view?.withheld;
  const withheldOld = withheld?.older_anonymiser || 0;
  const withheldOther = (withheld?.source_not_a_client || 0) + (withheld?.source_opted_out || 0);
  const refusedCount = view?.refused?.count || 0;
  const refusedKinds = Object.entries(view?.refused?.leaks || {}).map(([kind, n]) => `${n} ${kind}`).join(", ");

  const navContent = (
    <>
      <div style={{ height: 89, borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 10, padding: "0 24px", flexShrink: 0 }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: "var(--alpha-teal-10)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Library style={{ width: 16, height: 16, color: ACCENT }} />
        </div>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-weight-semibold)", lineHeight: 1.2 }}>Framework library</h2>
          <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>Admin · anonymised, by category</p>
        </div>
        {!isDesktop && (
          <button
            onClick={() => setDrawerOpen(false)}
            aria-label="Close categories menu"
            style={{ marginLeft: "auto", padding: 6, borderRadius: 7, border: "1px solid var(--border)", backgroundColor: "var(--muted)", cursor: "pointer", color: "var(--muted-foreground)", display: "flex" }}
          >
            <X style={{ width: 14, height: 14 }} />
          </button>
        )}
      </div>
      <nav style={{ flex: 1, overflowY: "auto", paddingTop: 8 }}>
        {view === null && !err && <div style={{ padding: "8px 24px" }}><Loading /></div>}
        <button
          onClick={() => { setShowPermissions(true); setDrawerOpen(false); }}
          style={{
            width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "10px 24px",
            backgroundColor: showPermissions ? "var(--muted)" : "transparent", border: "none",
            borderLeft: showPermissions ? `3px solid ${ACCENT}` : "3px solid transparent", cursor: "pointer", textAlign: "left",
            fontSize: "var(--text-sm)", fontWeight: showPermissions ? 600 : 500,
            color: showPermissions ? "var(--foreground)" : "var(--muted-foreground)",
          }}
        >
          <ShieldCheck style={{ width: 16, height: 16, flexShrink: 0 }} /> Client permissions
        </button>
        {categories.map((c) => {
          const isActive = !showPermissions && c.key === selected;
          const reads = c.templates.reduce((n, t) => n + t.reads, 0);
          return (
            <button
              key={c.key}
              onClick={() => { setSelected(c.key); setShowPermissions(false); setDrawerOpen(false); }}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "10px 24px",
                backgroundColor: isActive ? "var(--muted)" : "transparent", border: "none",
                borderLeft: isActive ? `3px solid ${ACCENT}` : "3px solid transparent", cursor: "pointer", textAlign: "left",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: "var(--text-sm)", fontWeight: isActive ? 600 : 500, color: isActive ? "var(--foreground)" : "var(--muted-foreground)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {categoryTitle(c)}
                </div>
                <div style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
                  {c.vertical_label || c.vertical || "No vertical"} · {c.templates.length} template{c.templates.length === 1 ? "" : "s"} · {reads} read{reads === 1 ? "" : "s"}
                </div>
              </div>
              {isActive && <ChevronRight style={{ width: 16, height: 16, color: ACCENT, flexShrink: 0 }} />}
            </button>
          );
        })}
      </nav>
    </>
  );

  return (
    <div data-kit className="flex" style={{ backgroundColor: "var(--background)" }}>
      {isDesktop ? (
        <aside style={{ width: 280, borderRight: "1px solid var(--border)", backgroundColor: "var(--background)", flexShrink: 0 }}>
          <div style={{ position: "sticky", top: 0, maxHeight: "100vh", overflowY: "auto", display: "flex", flexDirection: "column" }}>
            {navContent}
          </div>
        </aside>
      ) : (
        <>
          {drawerOpen && (
            <div onClick={() => setDrawerOpen(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 45 }} aria-hidden="true" />
          )}
          <div
            style={{
              position: "fixed", top: 0, left: 0, height: "100dvh", width: "min(320px, 85vw)", zIndex: 46,
              backgroundColor: "var(--background)", borderRight: "1px solid var(--border)", display: "flex",
              flexDirection: "column", overflow: "hidden", transform: drawerOpen ? "translateX(0)" : "translateX(-100%)",
              transition: "transform 300ms ease-in-out",
            }}
            aria-hidden={!drawerOpen}
          >
            {navContent}
          </div>
        </>
      )}

      <main className="flex-1" style={{ backgroundColor: "var(--background)", minWidth: 0, paddingBottom: 24 }}>
        {!isDesktop && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "20px 16px 0", padding: "10px 12px", borderRadius: 10, border: "1px solid var(--border)", backgroundColor: "var(--muted-30)" }}>
            <button
              onClick={() => setDrawerOpen(true)}
              aria-label="Open categories"
              style={{ padding: 8, borderRadius: 8, border: "1px solid var(--border)", backgroundColor: "var(--background)", cursor: "pointer", color: "var(--foreground)", display: "flex", flexShrink: 0 }}
            >
              <PanelLeft style={{ width: 16, height: 16 }} />
            </button>
            <div style={{ flex: 1, minWidth: 0, fontSize: "var(--text-sm)", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {showPermissions ? "Client permissions" : active ? categoryTitle(active) : "Pick a category"}
            </div>
          </div>
        )}
        <div className="px-4 md:px-6 py-5 md:py-6 flex flex-col gap-4">
          {err && <ErrorNote>{err}</ErrorNote>}
          {view && !view.available && (
            <ErrorNote>The framework library is not installed yet: apply db/migrations/2026-10-29_framework_library.sql, 2026-10-31_framework_library_consent.sql and 2026-11-01_framework_library_refusals.sql.</ErrorNote>
          )}
          {withheldOld > 0 && (
            <ErrorNote>
              {withheldOld} template{withheldOld === 1 ? " was" : "s were"} built by an earlier anonymiser and {withheldOld === 1 ? "is" : "are"} withheld
              from this view and from every generation. Run scripts/backfill_framework_library.py to rebuild {withheldOld === 1 ? "it" : "them"}.
            </ErrorNote>
          )}
          {withheldOther > 0 && (
            <p style={helpStyle}>
              {withheldOther} template{withheldOther === 1 ? "" : "s"} from a prospect, a demo brand or an opted-out client withheld; the backfill removes them.
            </p>
          )}
          {refusedCount > 0 && (
            <ErrorNote>
              {refusedCount} approved framework{refusedCount === 1 ? " was" : "s were"} refused by the anonymiser and made no template ({refusedKinds}).
              Run scripts/backfill_framework_library.py after a fix to add {refusedCount === 1 ? "it" : "them"}.
            </ErrorNote>
          )}
          <div className="flex justify-end">
            <Button disabled={refreshing || !view?.available} onClick={refresh} title="Recompute every template's performance from its reads and calibrations">
              <RefreshCw className={refreshing ? "animate-spin" : ""} style={{ width: 14, height: 14 }} /> Recompute performance
            </Button>
          </div>
          {showPermissions ? (
            <ClientPermissions clients={clients} onChanged={() => { load(); loadClients(); }} />
          ) : active ? (
            <CategoryView key={active.key} category={active} clients={clients} onChanged={load} onClientsChanged={loadClients} />
          ) : (
            <Section title="Pick a category" icon={<Library style={{ width: 16, height: 16, color: ACCENT }} />}>
              <p style={helpStyle}>
                Every client's approved framework becomes a template here, with the client stripped out, figures
                and proper nouns included: its beliefs and anchors, personas, and question battery, and how each
                measured. A new brand's Stage 2 generation
                starts from the best templates of its category; it still has to pass validation, the battery
                linter and Smoke calibration. Templates are history and cannot be edited; mark one as a
                recommended starting point, or exclude it.
              </p>
              {view?.available && categories.length === 0 && (
                <p style={{ ...helpStyle, marginTop: 8 }}>
                  The library is empty: no framework has been approved since it was installed. Run
                  scripts/backfill_framework_library.py to add the frameworks approved before.
                </p>
              )}
            </Section>
          )}
        </div>
      </main>
    </div>
  );
}
